import React from 'react';
import { useNavigate } from 'react-router-dom';
import { products } from '../mockData';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Products = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Our <span className="text-[#1A3E6E]">Products</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Ready-to-deploy SaaS solutions built with cutting-edge AI technology. 
              Transform your business operations with our industry-specific platforms.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {products.map((product, index) => (
              <div
                key={product.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-96 object-cover"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div>
                    <Badge className="bg-orange-100 text-[#1A3E6E] hover:bg-orange-200 mb-3">
                      {product.category}
                    </Badge>
                    <h2 className="text-4xl font-bold text-gray-900 mb-3">
                      {product.name}
                    </h2>
                    <p className="text-xl text-[#1A3E6E] font-semibold mb-4">
                      {product.tagline}
                    </p>
                    <p className="text-gray-600 leading-relaxed text-lg">
                      {product.description}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Key Features:</h3>
                    <div className="grid md:grid-cols-2 gap-2">
                      {product.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <CheckCircle className="text-[#1A3E6E] flex-shrink-0 mt-1" size={18} />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Use Cases:</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.useCases.map((useCase, idx) => (
                        <span
                          key={idx}
                          className="bg-orange-50 text-[#1A3E6E] px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {useCase}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1A3E6E] to-[#1A3E6E] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl mb-8 text-white/90">
            Contact us to learn more about our products and schedule a personalized demo.
          </p>
          <Button
            onClick={() => navigate('/contact')}
            variant="secondary"
            className="bg-white text-[#1A3E6E] hover:bg-gray-100 px-8 py-6 text-lg rounded-md transition-colors duration-200"
          >
            Contact Sales Team
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Products;
